import { httpsCallable } from "firebase/functions"
import { useCallback, useEffect, useState } from "react"
import type {
  PlaidLinkOnSuccess,
  PlaidLinkOnSuccessMetadata,
} from "react-plaid-link"
import { usePlaidLink } from "react-plaid-link"

import { functions } from "../firebase"

interface InitResult {
  linkToken: string
}

interface CompleteResult {
  count: number
}

const initPlaidConnection = httpsCallable<void, InitResult>(
  functions,
  "initPlaidConnection",
)
const completePlaidConnection = httpsCallable<
  {
    publicToken: string
    institutionName: string
    accounts: PlaidLinkOnSuccessMetadata["accounts"]
  },
  CompleteResult
>(functions, "completePlaidConnection")

export function usePlaidConnection(onConnected?: () => void) {
  const [linkToken, setLinkToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | undefined>()

  const onSuccess = useCallback<PlaidLinkOnSuccess>(
    async (publicToken, metadata) => {
      setLoading(true)
      setError(undefined)
      try {
        await completePlaidConnection({
          publicToken,
          institutionName: metadata.institution?.name ?? "Unknown",
          accounts: metadata.accounts,
        })
        setLinkToken(null) // reset so the hook can be reused
        onConnected?.()
      } catch (err) {
        setError(err as Error)
      } finally {
        setLoading(false)
      }
    },
    [onConnected],
  )

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
  })

  // Open Plaid Link as soon as the token is set and the SDK is ready
  useEffect(() => {
    if (linkToken && ready) {
      open()
      setLoading(false)
    }
  }, [linkToken, ready, open])

  const connect = useCallback(async () => {
    setLoading(true)
    setError(undefined)
    try {
      const result = await initPlaidConnection()
      setLinkToken(result.data.linkToken)
      // useEffect above will open Plaid Link once ready
    } catch (err) {
      setError(err as Error)
      setLoading(false)
    }
  }, [])

  return { connect, loading, error }
}
